import React, { Suspense, useState, FC, useEffect } from 'react';
import { filter, map } from 'lodash';
import { TabButtonStyled, RodRequestCaddyStyled, BoxStyled } from './styled';
import { caddyTabs } from './tab-data';
import { CaddyTab, CaddyTabType, RodRequestCaddyProps } from './types';
import { ErrorBoundary } from '@bechtel/client-common';
import {
  Spinner,
  SpinnerSize,
  SideSheet,
  SideSheetSize,
  SideSheetClose,
  SideSheetBody,
  SideSheetHeader,
  ButtonSize,
  useSideSheetState,
} from '@bechtel/gds';
import { useLingui } from '@lingui/react';
import { AddCaddy, IssuedCaddy } from './index';
import { RodRequestDetails } from '~/components/organisms/rod-request-details';
import { t } from '@lingui/macro';
import { UnsavedChangesDialog } from '~/components/organisms/unsaved-changes-dialog/unsaved-changes-dialog';
import { RodRequestCaddy as RRCaddy, RodRequestCaddyStatusCode, RodRequestStatusCode } from '@bechtel/types';

export const RodRequestCaddy: FC<RodRequestCaddyProps> = ({ rodRequestDetail }) => {
  const { i18n } = useLingui();
  const [selectedTab, setSelectedTab] = useState<CaddyTabType>(CaddyTabType.ADD);
  const { visible: visibleSidesheet, close: closeSidesheet } = useSideSheetState(false);
  const [caddyCount, setCaddyCount] = useState(0);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  const issuedCaddiesCount: number = filter(
    rodRequestDetail.caddies,
    (caddy: RRCaddy) => caddy.currentStatusI18n === RodRequestCaddyStatusCode.ISSUED
  )?.length;

  useEffect(() => {
    if (rodRequestDetail?.currentStatusI18n === RodRequestStatusCode.CLOSED) {
      setSelectedTab(CaddyTabType.ISSUED);
    }
  }, [rodRequestDetail?.currentStatusI18n]);
  const rodRequestProgressWeld = {
    id: rodRequestDetail?.id,
    requestNumber: rodRequestDetail?.requestNumber,
    welds: rodRequestDetail?.welds,
    stencils: rodRequestDetail?.stencils,
  };

  return (
    <>
      <BoxStyled data-testid="rod-request-caddy-details">
        <RodRequestCaddyStyled>
          {map(caddyTabs, (tabItem: CaddyTab, index) => {
            return (
              <TabButtonStyled
                data-testid={`caddy-tab-${index}`}
                selected={tabItem.value === selectedTab}
                onClick={() => {
                  if (caddyCount && !showUnsavedChangesDialog && tabItem.value === CaddyTabType.ISSUED) {
                    setShowUnsavedChangesDialog(true);
                  } else {
                    setSelectedTab(tabItem.value);
                  }
                }}
                key={tabItem.value}
                css={{
                  display:
                    tabItem.value === CaddyTabType.ADD &&
                    rodRequestDetail?.currentStatusI18n === RodRequestStatusCode.CLOSED
                      ? 'none'
                      : 'initial',
                }}
              >
                {i18n._(tabItem.label)}
                {tabItem.value === CaddyTabType.ISSUED ? ` (${issuedCaddiesCount})` : null}
              </TabButtonStyled>
            );
          })}
        </RodRequestCaddyStyled>
        {selectedTab == CaddyTabType.ADD && rodRequestDetail?.currentStatusI18n !== RodRequestStatusCode.CLOSED && (
          <ErrorBoundary>
            <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
              <>
                <AddCaddy
                  setSelectedTab={setSelectedTab}
                  welders={map(rodRequestDetail?.stencils, (item) => item.stencil)}
                  rodRequestId={rodRequestDetail?.id}
                  updateCaddyCount={(count) => setCaddyCount(count)}
                />
                {/* <Button
                  size={ButtonSize.SMALL}
                  appearance={ButtonAppearance.PRIMARY}
                  onClick={openSidesheet}
                  css={{ width: '100px' }}
                >
                  Details
                </Button> */}
              </>
            </Suspense>
          </ErrorBoundary>
        )}
        {selectedTab == CaddyTabType.ISSUED && (
          <ErrorBoundary>
            <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
              <IssuedCaddy
                caddies={rodRequestDetail?.caddies}
                welders={map(rodRequestDetail?.stencils, (item) => item.stencil)}
                requestNumber={rodRequestDetail.requestNumber}
                rodRequestProgressWeld={[rodRequestProgressWeld]}
              />
            </Suspense>
          </ErrorBoundary>
        )}
      </BoxStyled>
      {visibleSidesheet && (
        <SideSheet
          size={SideSheetSize.DEFAULT}
          visible
          style={{
            boxShadow: `0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)`,
            width: '275px',
          }}
        >
          <SideSheetHeader title={t`Details`}>
            <SideSheetClose onClick={closeSidesheet} size={ButtonSize.SMALL}></SideSheetClose>
          </SideSheetHeader>
          <SideSheetBody>
            <RodRequestDetails rodRequest={rodRequestDetail} />
          </SideSheetBody>
        </SideSheet>
      )}
      {showUnsavedChangesDialog && (
        <UnsavedChangesDialog
          onClose={(canProceed) => {
            setShowUnsavedChangesDialog(false);
            if (canProceed) {
              setSelectedTab(CaddyTabType.ISSUED);
            }
          }}
        />
      )}
    </>
  );
};
